import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Award, ShieldCheck, ShieldAlert } from "lucide-react";

const StatCard = ({ label, value, hint }) => (
  <div className="border border-border p-5 bg-card">
    <div className="kicker">{label}</div>
    <div className="display text-3xl mt-1">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [audit, setAudit] = useState([]);

  const load = async () => {
    try {
      const [s, u, p, e, d, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/vehicles", { params: { status: "requested" } }),
        api.get("/escrow/mine"),
        api.get("/disputes"),
        api.get("/admin/audit"),
      ]);
      setStats(s.data); setUsers(u.data); setPipeline(p.data);
      setEscrows(e.data); setDisputes(d.data); setAudit(a.data);
    } catch (err) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="kicker">Executive Dashboard</div>
            <h1 className="display text-4xl">THOM Verify™ Operations</h1>
          </div>
          <Link to="/command-centre" className="btn-outline" data-testid="cc-link">
            <span className="thom-seal">Command Centre™</span>
          </Link>
          <Link to="/network/intelligence" className="btn-outline" data-testid="ni-link">
            Network Intelligence
          </Link>
          <Link to="/geo-intelligence" className="btn-outline" data-testid="geo-link">
            Geographic Intel
          </Link>
          <Link to="/ops" className="btn-outline" data-testid="ops-link">
            Executive Ops
          </Link>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="Verified" value={stats.verified} hint={`of ${stats.total_vehicles} listings`} />
            <StatCard label="Pending" value={stats.pending_verification} hint="in verification pipeline" />
            <StatCard label="Escrow Balance" value={`$${Number(stats.escrow_balance_usd).toLocaleString()}`} hint={`${stats.open_escrows} active`} />
            <StatCard label="Open Disputes" value={stats.open_disputes} hint={`${stats.users} users`} />
          </div>
        )}

        <Tabs defaultValue="pipeline">
          <TabsList>
            <TabsTrigger value="pipeline" data-testid="admin-tab-pipeline">Trust Pipeline</TabsTrigger>
            <TabsTrigger value="intel" data-testid="admin-tab-intel">Intelligence</TabsTrigger>
            <TabsTrigger value="escrow" data-testid="admin-tab-escrow">Escrow</TabsTrigger>
            <TabsTrigger value="disputes" data-testid="admin-tab-disputes">Disputes</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="rules" data-testid="admin-tab-rules">Rules</TabsTrigger>
            <TabsTrigger value="plans" data-testid="admin-tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
          </TabsList>

          <TabsContent value="pipeline">
            <PipelinePanel items={pipeline} onChanged={load} users={users} />
          </TabsContent>

          <TabsContent value="intel">
            <IntelPanel />
          </TabsContent>

          <TabsContent value="escrow">
            <EscrowPanel items={escrows} onChanged={load} />
          </TabsContent>

          <TabsContent value="disputes">
            <DisputePanel items={disputes} onChanged={load} />
          </TabsContent>

          <TabsContent value="users">
            <UsersPanel items={users} onChanged={load} />
          </TabsContent>

          <TabsContent value="rules">
            <RulesPanel />
          </TabsContent>

          <TabsContent value="plans">
            <PlansPanel />
          </TabsContent>

          <TabsContent value="audit">
            <AuditPanel items={audit} />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function IntelPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { (async () => { try { const r = await api.get("/intel/overview"); setD(r.data); } catch {} })(); }, []);
  if (!d) return <div className="text-sm text-muted-foreground">Loading intelligence…</div>;
  const Sec = ({ title, children }) => (
    <div className="border border-border bg-card p-5">
      <div className="kicker mb-3">{title}</div>
      {children}
    </div>
  );
  const dist = d.trust.score_distribution;
  const maxDist = Math.max(1, ...Object.values(dist));
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Sec title="Operations">
        <div className="grid grid-cols-3 gap-3">
          <div><div className="display text-3xl">{d.operations.cases_total}</div><div className="text-xs text-muted-foreground">Cases total</div></div>
          <div><div className="display text-3xl text-amber-700">{d.operations.cases_open}</div><div className="text-xs text-muted-foreground">Open</div></div>
          <div><div className="display text-3xl text-primary">{d.operations.cases_closed}</div><div className="text-xs text-muted-foreground">Closed</div></div>
        </div>
      </Sec>
      <Sec title="Financial">
        <div className="grid grid-cols-2 gap-3">
          <div><div className="display text-3xl mono">${Number(d.financial.revenue_released_usd).toLocaleString()}</div><div className="text-xs text-muted-foreground">Released</div></div>
          <div><div className="display text-3xl mono">${Number(d.financial.escrow_balance_usd).toLocaleString()}</div><div className="text-xs text-muted-foreground">In escrow</div></div>
        </div>
      </Sec>
      <Sec title="Trust Intelligence">
        <div className="flex items-baseline gap-2">
          <div className="display text-4xl text-primary">{d.trust.average_trust_score}</div>
          <div className="text-xs text-muted-foreground">avg trust score</div>
        </div>
        <div className="mt-3 space-y-1">
          {Object.entries(dist).map(([k, v]) => (
            <div key={k} className="text-xs flex items-center gap-2">
              <div className="w-28 mono">{k.replace(/_/g, " ")}</div>
              <div className="flex-1 bg-secondary h-2 rounded-sm overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${(v/maxDist)*100}%` }} />
              </div>
              <div className="mono w-6 text-right">{v}</div>
            </div>
          ))}
        </div>
      </Sec>
      <Sec title="Certificates">
        <div className="grid grid-cols-3 gap-3">
          <div><div className="display text-3xl">{d.trust.certificates_issued}</div><div className="text-xs text-muted-foreground">Issued</div></div>
          <div><div className="display text-3xl text-primary">{d.trust.certificates_valid}</div><div className="text-xs text-muted-foreground">Valid</div></div>
          <div><div className="display text-3xl text-red-700">{d.trust.certificates_revoked}</div><div className="text-xs text-muted-foreground">Revoked</div></div>
        </div>
      </Sec>
      <Sec title="Geography">
        {d.geography.length === 0 ? <div className="text-xs text-muted-foreground">No data</div> : (
          <ul className="space-y-1 text-sm">
            {d.geography.slice(0, 6).map(g => <li key={g.city} className="flex justify-between border-b border-border py-1"><span>{g.city}</span><span className="mono">{g.listings}</span></li>)}
          </ul>
        )}
      </Sec>
      <Sec title="Inspector Productivity">
        {d.inspectors.length === 0 ? <div className="text-xs text-muted-foreground">No data</div> : (
          <ul className="space-y-1 text-sm">
            {d.inspectors.map(i => <li key={i.inspector_id} className="flex justify-between border-b border-border py-1"><span>{i.name}</span><span className="mono">{i.completed}</span></li>)}
          </ul>
        )}
      </Sec>
      <Sec title="Modules">
        <div className="grid grid-cols-2 gap-3">
          <div><div className="display text-3xl">{d.modules.auto_vehicles}</div><div className="text-xs text-muted-foreground">AutoVerify™</div></div>
          <div><div className="display text-3xl">{d.modules.property_assets}</div><div className="text-xs text-muted-foreground">PropertyVerify™</div></div>
        </div>
      </Sec>
      <Sec title="Customers">
        <div className="grid grid-cols-2 gap-3">
          <div><div className="display text-3xl">{d.customers.users_total}</div><div className="text-xs text-muted-foreground">Users</div></div>
          <div><div className="display text-3xl">{d.customers.dealers}</div><div className="text-xs text-muted-foreground">Dealers</div></div>
        </div>
      </Sec>
    </div>
  );
}

function RulesPanel() {
  const [rules, setRules] = useState([]);
  const [module, setModule] = useState("auto");
  const load = async () => {
    const r = await api.get("/intel/compliance-rules", { params: { module_key: module } });
    setRules(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [module]);
  const toggle = async (r) => {
    try {
      await api.patch(`/intel/compliance-rules/${r.id}`, null, { params: { enabled: !r.enabled } });
      toast.success(`Rule ${r.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className="kicker">Module</div>
        <Select value={module} onValueChange={setModule}>
          <SelectTrigger className="w-56" data-testid="rules-module-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">AutoVerify™</SelectItem>
            <SelectItem value="property">PropertyVerify™</SelectItem>
            <SelectItem value="business">BusinessVerify™</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">Rules are stored in Mongo — no redeploy required to change them.</div>
      </div>
      <div className="border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/60"><tr>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Rule</th>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Severity</th>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">On Fail</th>
            <th className="text-right py-2.5 px-3 text-xs uppercase tracking-wider">Enabled</th>
          </tr></thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id} className="border-t border-border">
                <td className="py-3 px-3">
                  <div className="font-medium">{r.label}</div>
                  <div className="text-xs mono text-muted-foreground">{r.rule_key}</div>
                </td>
                <td className="py-3 px-3"><span className="mono text-[10px] px-2 py-1 bg-secondary rounded-sm uppercase">{r.severity}</span></td>
                <td className="py-3 px-3"><span className={`mono text-[10px] px-2 py-1 rounded-sm uppercase ${r.on_fail === "reject" ? "bg-red-700 text-white" : "bg-amber-500 text-white"}`}>{r.on_fail}</span></td>
                <td className="py-3 px-3 text-right">
                  <Button size="sm" variant={r.enabled ? "default" : "outline"} onClick={() => toggle(r)} className={r.enabled ? "bg-primary hover:bg-primary/90" : ""} data-testid={`toggle-rule-${r.rule_key}`}>
                    {r.enabled ? "Enabled" : "Disabled"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlansPanel() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { (async () => { const r = await api.get("/intel/plans"); setPlans(r.data); })(); }, []);
  const TIER_BG = { bronze: "border-amber-700", silver: "border-slate-400", gold: "border-amber-500", platinum: "border-teal-600", diamond: "border-sky-600" };
  return (
    <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
      {plans.map(p => (
        <div key={p.key} className={`border-2 ${TIER_BG[p.key] || "border-border"} bg-card p-5`} data-testid={`plan-${p.key}`}>
          <div className="kicker">{p.label}</div>
          <div className="mt-2 flex items-baseline gap-1">
            <div className="display text-3xl">${p.price_usd_month}</div>
            <div className="text-xs text-muted-foreground">/mo</div>
          </div>
          <ul className="mt-4 space-y-1 text-xs">
            {Object.entries(p.entitlements || {}).slice(0, 8).map(([k, v]) => (
              <li key={k} className="flex justify-between border-b border-border py-1">
                <span className="text-muted-foreground">{k.replace(/_/g, " ")}</span>
                <span className="mono">{v === true ? "✓" : v === false ? "—" : v == null ? "∞" : String(v)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function PipelinePanel({ items, onChanged, users }) {
  const inspectors = users.filter(u => u.role === "inspector");
  const assign = async (vid, inspector_id) => {
    try {
      await api.post(`/inspections/assign`, null, { params: { vehicle_id: vid, inspector_id } });
      toast.success("Inspector assigned");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-secondary/60">
          <tr>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Vehicle</th>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Seller</th>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Status</th>
            <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Assign Inspector</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">No pending verification requests.</td></tr>}
          {items.map((v) => (
            <tr key={v.id} className="border-t border-border">
              <td className="py-3 px-3">{v.year} {v.make} {v.model} <span className="text-xs text-muted-foreground mono ml-2">{v.id}</span></td>
              <td className="py-3 px-3 text-sm">{v.seller?.name || v.seller_id}</td>
              <td className="py-3 px-3"><span className="mono text-[10px] px-2 py-1 bg-amber-500 text-white rounded-sm uppercase">{v.verification_status}</span></td>
              <td className="py-3 px-3">
                <Select onValueChange={(uid) => assign(v.id, uid)}>
                  <SelectTrigger className="w-56" data-testid={`assign-inspector-${v.id}`}><SelectValue placeholder="Assign inspector" /></SelectTrigger>
                  <SelectContent>
                    {inspectors.map((i) => <SelectItem key={i.user_id} value={i.user_id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EscrowPanel({ items, onChanged }) {
  const act = async (id, action) => {
    try {
      await api.post(`/escrow/${id}/${action}`, null, { params: { notes: "Admin action" } });
      toast.success(`Escrow ${action}d`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const [issueBusy, setIssueBusy] = useState(null);
  const issueCert = async (vehicle_id) => {
    setIssueBusy(vehicle_id);
    try {
      const r = await api.post(`/reports/vehicle/${vehicle_id}/issue`, null, { params: { validity_days: 90 } });
      toast.success(`Certificate ${r.data.certificate_number} issued`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setIssueBusy(null); }
  };

  return (
    <div>
      <div className="border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/60">
            <tr>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Escrow</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Vehicle</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Amount</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground text-sm">No escrows yet.</td></tr>}
            {items.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="py-3 px-3 mono text-xs">{e.payment_reference || e.id}</td>
                <td className="py-3 px-3 text-sm">{e.vehicle ? `${e.vehicle.year} ${e.vehicle.make} ${e.vehicle.model}` : e.vehicle_id}</td>
                <td className="py-3 px-3 mono">${Number(e.amount_usd).toLocaleString()}</td>
                <td className="py-3 px-3"><span className="mono text-[10px] px-2 py-1 bg-secondary rounded-sm uppercase">{e.status}</span></td>
                <td className="py-3 px-3">
                  {["committed", "verified", "disputed", "inspection_pending"].includes(e.status) && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" onClick={() => act(e.id, "release")} className="bg-primary hover:bg-primary/90" data-testid={`escrow-release-${e.id}`}>Release to Seller</Button>
                      <Button size="sm" variant="outline" onClick={() => act(e.id, "refund")} data-testid={`escrow-refund-${e.id}`}>Refund Buyer</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <h3 className="display text-2xl">Issue Trust Certificates</h3>
        <p className="text-sm text-muted-foreground mb-3">Verified vehicles can be certified with a QR-verifiable digital certificate.</p>
        <IssueCertificateList onIssue={issueCert} busy={issueBusy} />
      </div>
    </div>
  );
}

function IssueCertificateList({ onIssue, busy }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      const r = await api.get("/vehicles", { params: { only_verified: true, limit: 50 } });
      setItems(r.data);
    })();
  }, []);
  return (
    <div className="border border-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-secondary/60"><tr>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Vehicle</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Trust</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Certificate</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider"></th>
        </tr></thead>
        <tbody>
          {items.map(v => (
            <tr key={v.id} className="border-t border-border">
              <td className="py-3 px-3">{v.year} {v.make} {v.model}</td>
              <td className="py-3 px-3 mono">{v.trust_score ?? "—"}</td>
              <td className="py-3 px-3 mono text-xs">{v.certificate_number || "—"}</td>
              <td className="py-3 px-3">
                <Button size="sm" onClick={() => onIssue(v.id)} disabled={busy === v.id} className="bg-primary hover:bg-primary/90" data-testid={`issue-cert-${v.id}`}>
                  <Award className="h-4 w-4 mr-1" /> {busy === v.id ? "Issuing…" : v.certificate_number ? "Re-issue" : "Issue Certificate"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DisputePanel({ items, onChanged }) {
  const resolve = async (id, outcome) => {
    try {
      await api.post(`/disputes/${id}/resolve`, null, { params: { outcome, notes: "Resolved from admin" } });
      toast.success(`Dispute ${outcome}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-secondary/60"><tr>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Reason</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Status</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Details</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Resolve</th>
        </tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">No disputes.</td></tr>}
          {items.map(d => (
            <tr key={d.id} className="border-t border-border">
              <td className="py-3 px-3">{d.reason}</td>
              <td className="py-3 px-3"><span className="mono text-[10px] px-2 py-1 bg-red-700 text-white rounded-sm uppercase">{d.status}</span></td>
              <td className="py-3 px-3 text-xs text-muted-foreground max-w-md">{d.details}</td>
              <td className="py-3 px-3">
                {["open", "under_review"].includes(d.status) && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => resolve(d.id, "refund")} variant="outline">Refund</Button>
                    <Button size="sm" onClick={() => resolve(d.id, "release")} className="bg-primary hover:bg-primary/90">Release</Button>
                    <Button size="sm" onClick={() => resolve(d.id, "dismiss")} variant="ghost">Dismiss</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsersPanel({ items, onChanged }) {
  const setRole = async (uid, role) => {
    try {
      await api.post(`/admin/users/${uid}/role`, null, { params: { role } });
      toast.success("Role updated");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-secondary/60"><tr>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Name</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Email</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Provider</th>
          <th className="text-left py-2.5 px-3 text-xs uppercase tracking-wider">Role</th>
        </tr></thead>
        <tbody>
          {items.map(u => (
            <tr key={u.user_id} className="border-t border-border">
              <td className="py-3 px-3">{u.name}</td>
              <td className="py-3 px-3 text-sm">{u.email}</td>
              <td className="py-3 px-3 mono text-xs">{u.provider}</td>
              <td className="py-3 px-3">
                <Select value={u.role} onValueChange={(r) => setRole(u.user_id, r)}>
                  <SelectTrigger className="w-40" data-testid={`user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["buyer", "seller", "dealer", "inspector", "admin"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditPanel({ items }) {
  return (
    <div className="border border-border overflow-hidden max-h-[600px] overflow-y-auto">
      <table className="w-full">
        <thead className="bg-secondary/60 sticky top-0"><tr>
          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Time</th>
          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Actor</th>
          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Action</th>
          <th className="text-left py-2 px-3 text-xs uppercase tracking-wider">Target</th>
        </tr></thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id} className="border-t border-border">
              <td className="py-2 px-3 mono text-xs">{new Date(a.ts).toLocaleString()}</td>
              <td className="py-2 px-3 mono text-xs">{a.actor_role}:{a.actor_id.slice(-6)}</td>
              <td className="py-2 px-3">{a.action}</td>
              <td className="py-2 px-3 mono text-xs">{a.target_type}:{a.target_id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
